import { Paging, withSearch } from "@elastic/react-search-ui";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Button, Loading } from "components/common";
import {
  ScrollWrapper,
  SidebarWrapper
} from "components/search/buckets/common";
import { CodeSystem } from "context/elasticsearch/types";
import { useStoreContext } from "context/StoreProvider";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { codesystems, getCodeSystem, paths, scrollToTop } from "utils";

import { SidebarContent } from "../CodeSystemBrowserSection/style";
import { MetaInfoBar, SearchResultTable, SidebarFilter } from "./common";
import SmallLabResultView from "./common/SmallLabResultView";
import { BodyResultsProps } from "./interface";
import { isClinicalCodesystem, isSmallLabCodesystem } from "./utils";

type CodeSystemProp = {
  codeSystem: CodeSystem;
  displayScore: boolean;
};

const CodeSystemSearchResultSection = ({
  searchTerm,
  //resultSearchTerm,
  isLoading,
  codeSystem,
  displayScore,
  setFilter,
  removeFilter,
  addFilter
}: BodyResultsProps & CodeSystemProp) => {
  const [showFilter, setShowFilter] = useState<boolean>(
    isMobile || window.innerWidth < 992 ? false : true
  );
  const { t } = useTranslation();

  const { state } = useStoreContext();

  const { inputRef } = state;

  const { pathname } = useLocation();

  // Store filter functions in refs to avoid effect re-runs when references change
  const setFilterRef = useRef(setFilter);
  const addFilterRef = useRef(addFilter);
  const removeFilterRef = useRef(removeFilter);

  useEffect(() => {
    setFilterRef.current = setFilter;
    addFilterRef.current = addFilter;
    removeFilterRef.current = removeFilter;
  }, [setFilter, addFilter, removeFilter]);

  /* This checks if the searchTerm changes to something different than the first searchTerm,
  so that the content updates with the result if the user searches for something else  */

  useLayoutEffect(() => {
    if (
      getCodeSystem(pathname) === codesystems.icpc2 &&
      pathname !== paths.main
    ) {
      setFilterRef.current("code_system.enum", codesystems.icpc2, "any");
      addFilterRef.current("code_system.enum", codesystems.icpc2b, "any");
    }
    if (pathname.includes(paths.snomed)) {
      removeFilterRef.current("code_system.enum");
    }
  }, [pathname]);

  /* This makes sure the input gets refocused after the searchTerm state update */
  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, [inputRef, searchTerm]);

  const isMobileOrSmallScreen = isMobile || window.innerWidth < 992;

  const isSmallLab: boolean = useMemo(
    () => isSmallLabCodesystem(codeSystem),
    [codeSystem]
  );

  const isClinical: boolean = useMemo(
    () => isClinicalCodesystem(codeSystem),
    [codeSystem]
  );

  const isKis: boolean = useMemo(
    () => codeSystem === CodeSystem.Adm,
    [codeSystem]
  );

  return (
    <>
      <SidebarContent $size={isMobileOrSmallScreen || isClinical ? "sm" : "md"}>
        <div>
          <Row className="row-lg-cols-2 position-relative">
            {isMobileOrSmallScreen && (
              <Button
                variant="tertiary"
                icon={showFilter ? faArrowUp : faArrowDown}
                className="justify-content-between d-flex mt-2 mt-lg-0"
                size="sm"
                iconright
                color="grey"
                onClick={() => setShowFilter(!showFilter)}
                textColor="black"
              >
                {showFilter
                  ? t("nlkPage.filter.hideFilter")
                  : t("nlkPage.filter.showFilter")}
              </Button>
            )}

            <SidebarWrapper
              className={`${
                showFilter ? "d-flex flex-column gap-3" : "d-none"
              } ps-0 pt-2 pb-2 pt-lg-0 pb-lg-0`}
              xs={12}
              lg={3}
            >
              <SidebarFilter
                //searchTerm={searchTerm || ""}
                codeSystem={codeSystem}
              />
            </SidebarWrapper>

            <Col
              className="p-0 position-relative bg-white" //neccessary?
              lg={9}
              id="search-results"
              tabIndex={0}
            >
              {isLoading && <Loading />}
              {!isLoading && <MetaInfoBar />}
              {isSmallLab && !isLoading && (
                <>
                  <ScrollWrapper id="lab-result-wrapper">
                    <SmallLabResultView />
                  </ScrollWrapper>
                </>
              )}
              {/* Fordi her skal ikke søkeresultatene vises før søk - reversert av DEHPKTSD-237, hvis det ikke skal vises resultater før søk, fjern kommentar under */}
              {!isSmallLab &&
                /* resultSearchTerm !== '' && */
                !isLoading && (
                  <>
                    <ScrollWrapper id="result-wrapper" $isAdm={isKis}>
                      <SearchResultTable
                        codeSystem={codeSystem}
                        displayScore={displayScore}
                      />
                    </ScrollWrapper>
                    <Row onClick={scrollToTop} className="py-2">
                      <Paging />
                    </Row>
                  </>
                )}
            </Col>
          </Row>
        </div>
      </SidebarContent>
    </>
  );
};

export default withSearch<CodeSystemProp, Partial<BodyResultsProps>>(
  ({
    searchTerm,
    resultSearchTerm,
    isLoading,
    setFilter,
    removeFilter,
    addFilter
  }) => ({
    searchTerm,
    resultSearchTerm,
    isLoading,
    setFilter,
    removeFilter,
    addFilter
  })
)(CodeSystemSearchResultSection);
